// src/arca_Auth/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing authentication on load
  useEffect(() => {
    console.log('AuthContext: Checking for existing authentication...');

    // Check for the new arcaUser token first
    const savedUser = localStorage.getItem('arcaUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('AuthContext: Found saved user data:', userData);

        // Ensure the corresponding token is also stored
        if (userData.token) {
          const userType = userData.userType || userData.role;
          const tokenKey = userType === 'admin' || userType === 'super_admin'
            ? 'adminToken'
            : userType === 'country_director'
              ? 'directorToken'
              : 'userToken';

          const existingToken = localStorage.getItem(tokenKey);
          if (!existingToken) {
            console.log(`AuthContext: Restoring ${tokenKey} from saved user data`);
            localStorage.setItem(tokenKey, userData.token);
          }
        }

        setUser(userData);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('arcaUser');
      }
    }

    // Check for existing tokens from the original AuthForms
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('userToken');
    const directorToken = localStorage.getItem('directorToken');

    if (adminToken === 'demo-admin-token') {
      console.log('AuthContext: Found admin token');
      setUser({
        id: 1,
        username: 'admin',
        email: 'admin@africaresp.org',
        firstName: 'Demo',
        lastName: 'Admin',
        userType: 'admin'
      });
      setIsAuthenticated(true);
    } else if (directorToken === 'demo-director-token') {
      console.log('AuthContext: Found director token');
      setUser({
        id: 3,
        username: 'director',
        email: 'director@africaresp.org',
        firstName: 'Demo',
        lastName: 'Director',
        userType: 'country_director'
      });
      setIsAuthenticated(true);
    } else if (userToken === 'demo-user-token') {
      console.log('AuthContext: Found user token');
      setUser({
        id: 2,
        email: 'user@africaresp.org',
        firstName: 'Demo',
        lastName: 'User',
        userType: 'user'
      });
      setIsAuthenticated(true);
    } else {
      console.log('AuthContext: No valid tokens found');
    }

    setLoading(false);
  }, []);

  // Handle authentication success
  const login = (userData) => {
    console.log('Login called with userData:', userData);
    console.log('🔑 Token from userData:', userData.token);
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('arcaUser', JSON.stringify(userData));

    // Store token separately based on user type
    if (userData.token) {
      const userType = userData.userType || userData.role;

      console.log('👤 User type detected for token storage:', userType);

      if (userType === 'admin' || userType === 'super_admin') {
        localStorage.setItem('adminToken', userData.token);
        console.log('✅ Admin token stored in localStorage');
        console.log('🔍 Stored adminToken:', localStorage.getItem('adminToken'));
      } else if (userType === 'country_director') {
        localStorage.setItem('directorToken', userData.token);
        console.log('Director token stored');
      } else {
        localStorage.setItem('userToken', userData.token);
        console.log('User token stored');
      }
    } else {
      console.error('❌ No token found in userData!');
    }

    console.log('Login completed - user authenticated');
  };

  // Handle sign out
  const logout = () => {
    console.log('Logout called - clearing authentication state');

    // Clear state first
    setUser(null);
    setIsAuthenticated(false);

    // Clear all possible tokens
    localStorage.removeItem('arcaUser');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('userToken');
    localStorage.removeItem('directorToken');

    console.log('Logout completed - user should be signed out');
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('arcaUser', JSON.stringify(userData));
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;